import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import {ActivatedRoute, RedirectCommand, ResolveFn, Router, RouterLink} from '@angular/router';
import { ContentLandingPage, LandingPageApiService } from './landing-page-api';
import { MatButton } from '@angular/material/button';
import { FormBuilder, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatError, MatFormField, MatInput, MatLabel } from '@angular/material/input';
import { DatePipe } from '@angular/common';
import { catchError, of } from 'rxjs';
import { DasLayoutFormComponent } from '@dwa/daslayout-editor';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonToggle, MatButtonToggleGroup } from '@angular/material/button-toggle';
import { MatCard, MatCardContent, MatCardHeader, MatCardTitle } from '@angular/material/card';
import { SnackbarService } from '../../../../core/services/snackbar.service';
import { HttpErrorResponse } from '@angular/common/http';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { take } from 'rxjs/operators';
import { MatIcon } from '@angular/material/icon';
import { TruncatePipe } from '../../pipes/truncate';
import { QueryPathRouteEncoder } from './landing-page-table';

export const landingPageResolver: ResolveFn<ContentLandingPage> = (route) => {
  const landingPageId = route.paramMap.get('landingPageId');
  const router = inject(Router);
  return inject(LandingPageApiService)
    .getLandingPage(QueryPathRouteEncoder.decode(landingPageId))
    .pipe(catchError(() => of(new RedirectCommand(router.parseUrl('/content/landing-pages')))));
};

@Component({
  imports: [
    MatButton,
    ReactiveFormsModule,
    MatFormField,
    MatFormField,
    DatePipe,
    DasLayoutFormComponent,
    MatFormField,
    MatFormField,
    MatLabel,
    MatError,
    MatInput,
    MatFormField,
    MatFormFieldModule,
    MatButtonToggleGroup,
    MatButtonToggle,
    MatCard,
    MatCardHeader,
    MatCardTitle,
    MatCardContent,
    MatIcon,
    RouterLink,
  ],

  template: `
    <header class="flex items-end justify-between">
      <a mat-button routerLink="/content/landing-pages">
        <mat-icon class="text-gray-700!">arrow_back</mat-icon>
        <span class="text-gray-700 font-normal tracking-normal">
        Back to all landing pages
          </span>
      </a>
      <div>
        @if (pageType === 'existing') {
          <div class="mb-1 text-right text-xs text-gray-600">
            Last modified: {{ landingPage().lastModifiedDate | date: 'dd.MM.yyyy HH:mm' }}
          </div>
        }
        <div class="flex justify-end gap-2">
          @if (pageType === 'existing') {
            <button class="w-24" mat-stroked-button color="warn" (click)="openDeleteDialog()">
              <mat-icon color="warn">delete</mat-icon>
              Delete
            </button>

            <button (click)="reset()" mat-stroked-button class="w-24" [disabled]="form.pristine" type="submit">
              <mat-icon>refresh</mat-icon>
              Reset
            </button>
          }

          <button
            (click)="save()"
            mat-stroked-button
            class="button-primary w-24"
            [disabled]="form.invalid || form.pristine"
            type="submit"
          >
            {{ pageType === 'existing' ? 'Update' : 'Create' }}
          </button>
        </div>
      </div>
    </header>

    <form [formGroup]="form">
      <mat-card appearance="outlined" class="my-5">
        <mat-card-header>
          <mat-card-title>Base information</mat-card-title>
        </mat-card-header>
        <mat-card-content>
          <div class="mt-5 grid max-w-5xl grid-cols-6 gap-x-4">
            <mat-form-field class="col-span-4 mb-6" appearance="outline">
              <mat-label>URL Path</mat-label>
              <input placeholder="vw/vw-golf" matInput formControlName="queryPath" />
              <mat-hint>URL path following www.dasweltauto.at/s/. E.g. 'vw/vw-golf'.</mat-hint>
              @if (form.controls.queryPath?.invalid && form.controls.queryPath?.touched) {
                <mat-error> Query Path is required</mat-error>
              }
            </mat-form-field>

            <mat-button-toggle-group
              class="col-span-2 h-12 w-fit justify-self-end"
              formControlName="published"
              aria-label="Publication state"
            >
              <mat-button-toggle [value]="false">Draft</mat-button-toggle>
              <mat-button-toggle [value]="true">Published</mat-button-toggle>
            </mat-button-toggle-group>

            <mat-form-field class="col-span-6" appearance="outline">
              <mat-label>SEO Title</mat-label>
              <input matInput formControlName="seoTitle" />
              @if (form.controls.seoTitle?.invalid && form.controls.seoTitle?.touched) {
                <mat-error> SEO Title is required</mat-error>
              }
            </mat-form-field>

            <mat-form-field class="col-span-6" appearance="outline">
              <mat-label>SEO Description</mat-label>
              <textarea matInput formControlName="seoDescription"></textarea>
              @if (form.controls.seoDescription?.invalid && form.controls.seoDescription?.touched) {
                <mat-error> SEO Description is required</mat-error>
              }
            </mat-form-field>
          </div>
        </mat-card-content>
      </mat-card>

      <mat-card appearance="outlined">
        <mat-card-header>
          <mat-card-title>Markup</mat-card-title>
        </mat-card-header>
        <mat-card-content>
          <daslayout-editor formControlName="html" />
        </mat-card-content>
      </mat-card>
    </form>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LandingPageDetailComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly landingPageService = inject(LandingPageApiService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly dialog = inject(MatDialog);
  private readonly formBuilder = inject(FormBuilder);

  protected readonly landingPage = signal<ContentLandingPage>(this.route.snapshot.data.landingPage);
  protected readonly pageType: 'new' | 'existing' = this.route.snapshot.paramMap.get('landingPageId')
    ? 'existing'
    : 'new';
  protected readonly form = this.buildForm();

  protected save() {
    const request: Partial<ContentLandingPage> = {
      queryPath: this.form.value.queryPath || this.form.getRawValue().queryPath, // access disabled values
      html: this.form.value.html,
      seoDescription: this.form.value.seoDescription,
      seoTitle: this.form.value.seoTitle,
      published: !!this.form.value.published,
    };

    if (this.pageType === 'new') {
      this.landingPageService.createLandingPage(request as ContentLandingPage).subscribe({
        next: (value) =>
          this.router.navigate(['../', QueryPathRouteEncoder.encode(value.queryPath)], { relativeTo: this.route }),
      });
    } else {
      this.landingPageService.updateLandingPage(request as ContentLandingPage).subscribe({
        next: (data) => {
          this.onUpdateSuccess(data);
        },
        error: (err) => this.onUpdateError('Could not update landing page', err),
      });
    }
  }

  /**
   * Resets the form to the initial values from `landingPage`.
   *
   * TODO: Applies a workaround for a known issue (see [DWA-18443](https://jira.porscheinformatik.com/jira/browse/DWA-18443)).
   *
   * Throws an error if `landingPage` is null.
   */
  protected reset() {
    const landingPage = this.landingPage();
    if (!landingPage) {
      throw Error('Landing page must not be null');
    }

    // Workaround
    this.form.reset({ html: '' });
    this.form.reset({
      queryPath: landingPage.queryPath,
      seoTitle: landingPage.seoTitle,
      seoDescription: landingPage.seoDescription,
      html: landingPage.html,
      published: landingPage.published,
    });
  }

  protected openDeleteDialog() {
    const dialogRef = this.dialog.open(LandingPageConfirmDeleteComponent, {
      width: '600px',
      data: { itemName: this.landingPage().queryPath },
    });

    dialogRef.afterClosed().subscribe((deleteConfirmed) => {
      if (deleteConfirmed) {
        this.landingPageService
          .deleteLandingPage(this.landingPage().queryPath)
          .pipe(take(1))
          .subscribe({
            next: () => this.router.navigate(['../'], { relativeTo: this.route }),
            error: (err) => this.onUpdateError('Could not delete landing page', err),
          });
      }
    });
  }

  private onUpdateSuccess(data: ContentLandingPage) {
    this.snackbarService.openSnackBar('Landing page successfully updated.');
    this.landingPage.set(data);
    this.form.markAsUntouched();
  }

  private onUpdateError(description: string, err: HttpErrorResponse) {
    this.snackbarService.openSnackBar(`${description}, reason: ${err.error.detail}`, false, 6000);
  }

  private buildForm() {
    const landingPage = this.landingPage();
    const queryPathControl = new FormControl(
      { value: landingPage?.queryPath, disabled: this.pageType === 'existing' },
      [Validators.required],
    );

    return this.formBuilder.group({
      queryPath: queryPathControl,
      seoTitle: [landingPage?.seoTitle, [Validators.required]],
      seoDescription: [landingPage?.seoDescription, [Validators.required]],
      html: [landingPage?.html, [Validators.required]],
      published: [landingPage?.published || false],
    });
  }
}

@Component({
  imports: [MatButton, MatDialogTitle, MatDialogContent, MatDialogActions, TruncatePipe],
  template: `
    <h2 mat-dialog-title [title]="data.itemName">Delete (landing page) "{{ data.itemName | truncate: 30 }}"?</h2>
    <mat-dialog-content>
      <p>
        Are you sure you want to delete: <strong [title]="data.itemName">{{ data.itemName | truncate: 30 }}</strong
        >? This action cannot be undone.
      </p>
    </mat-dialog-content>

    <mat-dialog-actions align="end">
      <button mat-button (click)="onCancel()">Cancel</button>
      <button mat-raised-button color="warn" (click)="onConfirm()">Delete</button>
    </mat-dialog-actions>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
class LandingPageConfirmDeleteComponent {
  private readonly dialogRef = inject(MatDialogRef);
  protected readonly data: { itemName: string } = inject<{ itemName: string }>(MAT_DIALOG_DATA);

  protected onCancel(): void {
    this.dialogRef.close(false);
  }

  protected onConfirm(): void {
    this.dialogRef.close(true);
  }
}
